import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { platformApi, type UserInviteState } from '../../../lib/api/platform.api';
import { errorText } from '../../../lib/error-text';
import { formatDateTime } from '../../../lib/i18n-format';

// ─── La actividad de una cuenta (solo plataforma, solo lectura) ─────────────
// Para acompañar el ingreso de alguien: si activó su cuenta y cuándo, su
// último ingreso, sus cargos en cada seminario, en qué quedaron sus enlaces y
// sus últimas acciones relevantes (notas, actas, accesos dados). Decisión de
// Ricardo, 2026-10-30. Abrirla deja un evento de auditoría en el servidor.

const INVITE_COLOR: Record<UserInviteState, 'success' | 'warning' | 'default' | 'info'> = {
  used: 'success',
  pending: 'info',
  expired: 'warning',
  superseded: 'default',
};

export function UserActivityDialog({
  uid,
  name,
  onClose,
}: {
  uid: string | null;
  name: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const q = useQuery({
    queryKey: ['user-activity', uid],
    enabled: !!uid,
    // Cada apertura es una consulta auditada: no se reintenta ni se refresca
    // sola, para no dejar eventos que nadie pidió.
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: () => platformApi.getUserActivity(uid!),
  });
  const a = q.data;
  const when = (iso: string | null, empty: string) => (iso ? formatDateTime(iso) : empty);

  return (
    <Dialog open={!!uid} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('usersPage.activity.title', { name })}
        <Typography variant="body2" color="text.secondary">
          {t('usersPage.activity.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {q.isError && <Alert severity="error">{errorText(q.error)}</Alert>}
        {a && (
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('usersPage.activity.activation')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {a.activated
                    ? a.activatedAt
                      ? t('usersPage.activity.activatedOn', { date: formatDateTime(a.activatedAt) })
                      : t('usersPage.activity.activatedUndated')
                    : t('usersPage.activity.notActivated')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('usersPage.activity.lastSignIn')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {when(a.lastSignInAt, t('usersPage.activity.never'))}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('usersPage.activity.accountCreated')}
                </Typography>
                <Typography variant="body2">{when(a.createdAt, '—')}</Typography>
              </Box>
              {a.disabled && (
                <Alert severity="warning" sx={{ alignSelf: 'start' }}>
                  {t('usersPage.activity.disabled')}
                </Alert>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('usersPage.activity.memberships')}
              </Typography>
              {a.memberships.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('usersPage.activity.noMemberships')}
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {a.memberships.map((m) => (
                    <Stack
                      key={m.tenantId}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {m.tenantName}
                      </Typography>
                      {m.roles.map((r) => (
                        <Chip key={r} size="small" label={t(`usersPage.roleMeta.${r}.label`, r)} />
                      ))}
                      <Chip
                        size="small"
                        variant="outlined"
                        color={m.status === 'active' ? 'success' : 'default'}
                        label={t(`usersPage.activity.membershipStatus.${m.status}`, m.status)}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('usersPage.activity.invites')}
              </Typography>
              {a.invites.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('usersPage.activity.noInvites')}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('usersPage.activity.inviteCols.purpose')}</TableCell>
                        <TableCell>{t('usersPage.activity.inviteCols.seminary')}</TableCell>
                        <TableCell>{t('usersPage.activity.inviteCols.state')}</TableCell>
                        <TableCell>{t('usersPage.activity.inviteCols.expires')}</TableCell>
                        <TableCell>{t('usersPage.activity.inviteCols.used')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {a.invites.map((i, idx) => (
                        <TableRow key={`${i.tenantId}-${i.createdAt ?? idx}`}>
                          <TableCell>{t(`usersPage.activity.purpose.${i.purpose}`)}</TableCell>
                          <TableCell>{i.tenantName}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={INVITE_COLOR[i.state]}
                              label={t(`usersPage.activity.inviteState.${i.state}`)}
                            />
                          </TableCell>
                          <TableCell>{when(i.expiresAt, '—')}</TableCell>
                          <TableCell>
                            {i.state === 'used'
                              ? when(i.usedAt, t('usersPage.activity.undated'))
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('usersPage.activity.actions')}
              </Typography>
              {a.recentActions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('usersPage.activity.noActions')}
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {a.recentActions.map((x, idx) => (
                    <Stack
                      key={`${x.at}-${idx}`}
                      direction="row"
                      spacing={1.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        {formatDateTime(x.at)}
                      </Typography>
                      <Typography variant="body2">
                        {t(`usersPage.activity.action.${x.action}`, x.action)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {x.tenantName}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('usersPage.activity.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
